import React, { useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import "../../styles/admin/IssueGiftcards.css";

interface GiftcardRequest {
  productId: string;
  targetUserId?: string;
  claimCode?: string;
  amount?: number;
  quantity?: number;
  notes?: string;
}

interface IssuanceResult {
  id: string;
  status: "pending" | "success" | "failed";
  targetUser?: string;
  amount?: number;
  timestamp: Date;
  message?: string;
  claimCode?: string;
}

const PRODUCTS = [
  { id: "giftcard-500", name: "🎁 Giftcard 500 NOK", type: "GIFTCARD", amount: 500 },
  { id: "giftcard-1000", name: "🎁 Giftcard 1000 NOK", type: "GIFTCARD", amount: 1000 },
  { id: "giftcard-2000", name: "🎁 Giftcard 2000 NOK", type: "GIFTCARD", amount: 2000 },
  { id: "tokens-10", name: "🎟️ Range Tokens (10)", type: "RANGE_TOKEN", quantity: 10 },
  { id: "tokens-50", name: "🎟️ Range Tokens (50)", type: "RANGE_TOKEN", quantity: 50 },
  { id: "greenfee-9", name: "⛳ 9-Hole Greenfee", type: "GREENFEE_TICKET", quantity: 1 },
  { id: "greenfee-18", name: "⛳ 18-Hole Greenfee", type: "GREENFEE_TICKET", quantity: 1 },
];

export function IssueGiftcardsPage() {
  const [mode, setMode] = useState<"single" | "bulk">("single");
  const [formData, setFormData] = useState({
    productId: PRODUCTS[0].id,
    targetEmail: "",
    generateCode: true,
    quantity: 1,
    notes: "",
  });
  const [results, setResults] = useState<IssuanceResult[]>([]);
  const [issuingId, setIssuingId] = useState<string | null>(null);
  const [bulkText, setBulkText] = useState("");

  const functions = getFunctions();

  const handleIssueGiftcard = async (e: React.FormEvent) => {
    e.preventDefault();
    const requestId = uuidv4();
    setIssuingId(requestId);

    const result: IssuanceResult = {
      id: requestId,
      status: "pending",
      timestamp: new Date(),
    };

    setResults((prev) => [result, ...prev]);

    try {
      const issueGiftcardFn = httpsCallable(functions, "issue_giftcard");
      const response = await issueGiftcardFn({
        idempotencyKey: uuidv4(),
        productId: formData.productId,
        targetUserId: formData.targetEmail || undefined,
        claimCode: formData.generateCode ? uuidv4() : undefined,
        notes: formData.notes,
      });

      const data = response.data as any;
      if (data.success) {
        setResults((prev) =>
          prev.map((r) =>
            r.id === requestId
              ? {
                  ...r,
                  status: "success",
                  message: `✅ Giftcard issued successfully`,
                  claimCode: data.claimCode,
                  amount: formData.productId.includes("giftcard") 
                    ? PRODUCTS.find(p => p.id === formData.productId)?.amount
                    : undefined,
                }
              : r
          )
        );
        // Reset form
        setFormData({
          ...formData,
          targetEmail: "",
          generateCode: true,
          notes: "",
        });
      } else {
        throw new Error(data.error || "Failed to issue giftcard");
      }
    } catch (error: any) {
      setResults((prev) =>
        prev.map((r) =>
          r.id === requestId
            ? {
                ...r,
                status: "failed",
                message: error.message || "Failed to issue giftcard",
              }
            : r
        )
      );
    } finally {
      setIssuingId(null);
    }
  };

  const handleBulkIssue = async () => {
    const lines = bulkText
      .trim()
      .split("\n")
      .filter((line) => line.trim());

    if (lines.length === 0) {
      alert("❌ Please paste email addresses or codes");
      return;
    }

    setIssuingId("bulk");

    for (const line of lines) {
      const requestId = uuidv4();
      const result: IssuanceResult = {
        id: requestId,
        status: "pending",
        targetUser: line.trim(),
        timestamp: new Date(),
      };

      setResults((prev) => [result, ...prev]);

      try {
        const issueGiftcardFn = httpsCallable(functions, "issue_giftcard");
        const response = await issueGiftcardFn({
          idempotencyKey: uuidv4(),
          productId: formData.productId,
          targetUserId: line.trim(),
          claimCode: formData.generateCode ? uuidv4() : undefined,
          notes: `Bulk issue: ${formData.notes}`,
        });

        const data = response.data as any;
        if (data.success) {
          setResults((prev) =>
            prev.map((r) =>
              r.id === requestId
                ? {
                    ...r,
                    status: "success",
                    message: "✅ Issued",
                    claimCode: data.claimCode,
                  }
                : r
            )
          );
        } else {
          throw new Error(data.error);
        }
      } catch (error: any) {
        setResults((prev) =>
          prev.map((r) =>
            r.id === requestId
              ? {
                  ...r,
                  status: "failed",
                  message: error.message || "Failed",
                }
              : r
          )
        );
      }
    }

    setIssuingId(null);
    setBulkText("");
  };

  const product = PRODUCTS.find((p) => p.id === formData.productId);

  return (
    <div className="issue-giftcards-page">
      <div className="page-header">
        <h1>🎁 Issue Giftcards</h1>
        <p className="page-subtitle">
          Create and distribute giftcards, tokens, and greenfees to club members
        </p>
      </div>

      <div className="issue-container">
        {/* Mode Selector */}
        <div className="mode-selector">
          <button
            className={`mode-btn ${mode === "single" ? "active" : ""}`}
            onClick={() => setMode("single")}
          >
            📄 Single Issue
          </button>
          <button
            className={`mode-btn ${mode === "bulk" ? "active" : ""}`}
            onClick={() => setMode("bulk")}
          >
            📊 Bulk Import
          </button>
        </div>

        {/* Single Issue Mode */}
        {mode === "single" && (
          <div className="issue-form-section">
            <div className="form-card">
              <h2>Issue Giftcard</h2>

              <form onSubmit={handleIssueGiftcard}>
                {/* Product Selection */}
                <div className="form-group">
                  <label htmlFor="productId">📦 Product Type</label>
                  <select
                    id="productId"
                    value={formData.productId}
                    onChange={(e) =>
                      setFormData({ ...formData, productId: e.target.value })
                    }
                    className="form-input"
                  >
                    {PRODUCTS.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Target User */}
                <div className="form-group">
                  <label htmlFor="targetEmail">👤 Recipient Email (optional)</label>
                  <input
                    id="targetEmail"
                    type="email"
                    placeholder="user@example.com"
                    value={formData.targetEmail}
                    onChange={(e) =>
                      setFormData({ ...formData, targetEmail: e.target.value })
                    }
                    className="form-input"
                  />
                  <small>Leave empty to generate an unclaimed code</small>
                </div>

                {/* Code Generation */}
                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.generateCode}
                      onChange={(e) =>
                        setFormData({ ...formData, generateCode: e.target.checked })
                      }
                    />
                    🔐 Generate claim code
                  </label>
                  <small>If unchecked, giftcard is assigned directly to recipient</small>
                </div>

                {/* Notes */}
                <div className="form-group">
                  <label htmlFor="notes">📝 Notes</label>
                  <textarea
                    id="notes"
                    placeholder="Optional notes (campaign, reason, etc.)"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    className="form-input"
                    rows={3}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={issuingId !== null}
                  className="btn-submit"
                >
                  {issuingId ? "⏳ Issuing..." : "✨ Issue Giftcard"}
                </button>
              </form>

              {/* Product Info */}
              {product && (
                <div className="product-info">
                  <h3>Product Details</h3>
                  <p>
                    <strong>Type:</strong> {product.type}
                  </p>
                  {product.amount && (
                    <p>
                      <strong>Amount:</strong> {product.amount} NOK
                    </p>
                  )}
                  {product.quantity && (
                    <p>
                      <strong>Quantity:</strong> {product.quantity}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bulk Issue Mode */}
        {mode === "bulk" && (
          <div className="bulk-issue-section">
            <div className="form-card">
              <h2>Bulk Issue Giftcards</h2>

              <div className="form-group">
                <label htmlFor="productId">📦 Product Type</label>
                <select
                  value={formData.productId}
                  onChange={(e) =>
                    setFormData({ ...formData, productId: e.target.value })
                  }
                  className="form-input"
                >
                  {PRODUCTS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="bulkText">
                  📋 Email Addresses (one per line)
                </label>
                <textarea
                  id="bulkText"
                  placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  className="form-input"
                  rows={10}
                />
                <small>{bulkText.trim().split("\n").filter((l) => l.trim()).length} emails entered</small>
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.generateCode}
                    onChange={(e) =>
                      setFormData({ ...formData, generateCode: e.target.checked })
                    }
                  />
                  🔐 Generate claim codes
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="notes">📝 Campaign Notes</label>
                <textarea
                  id="notes"
                  placeholder="e.g., Christmas campaign 2025"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className="form-input"
                  rows={3}
                />
              </div>

              <button
                onClick={handleBulkIssue}
                disabled={issuingId !== null || bulkText.trim().length === 0}
                className="btn-submit"
              >
                {issuingId ? "⏳ Processing..." : "🚀 Issue All"}
              </button>
            </div>
          </div>
        )}

        {/* Results Section */}
        {results.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>📊 Issuance Results</h3>
              <span className="results-count">
                {results.filter((r) => r.status === "success").length} /
                {results.length} issued
              </span>
            </div>

            <div className="results-list">
              {results.map((result) => (
                <div key={result.id} className={`result-item result-${result.status}`}>
                  <div className="result-icon">
                    {result.status === "pending" && "⏳"}
                    {result.status === "success" && "✅"}
                    {result.status === "failed" && "❌"}
                  </div>

                  <div className="result-content">
                    <div className="result-target">
                      {result.targetUser || result.amount || "Giftcard"}
                    </div>
                    {result.message && (
                      <div className="result-message">{result.message}</div>
                    )}
                    {result.claimCode && (
                      <div className="result-code">
                        <span className="label">Code:</span>
                        <code>{result.claimCode}</code>
                        <button
                          className="btn-copy"
                          onClick={() => {
                            navigator.clipboard.writeText(result.claimCode!);
                          }}
                        >
                          📋
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="result-time">
                    {result.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
